import React from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LogoTile from "@/components/LogoTile";
import {
  JOB_PLATFORMS, JOB_TIPS,
  STUDENT_BANKS, STUDENT_LOANS, LOAN_TIPS,
  MONEY_TIPS, SAVINGS_APPS,
} from "@/data/studentData";
import { Briefcase, Wallet, PiggyBank, Coins, Lightbulb, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";

export default function JobsArgent() {
  return (
    <div className="space-y-10" data-testid="jobs-argent-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Job & argent</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Bosser & gérer ton budget</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Trouver un job étudiant, ouvrir un compte adapté, financer tes études et faire durer ton budget : tout ce que tu dois connaître pour ne plus jamais finir le mois en yoyo.
        </p>
      </header>

      <Tabs defaultValue="jobs" className="w-full">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="jobs" data-testid="tab-jobs"><Briefcase size={14} className="mr-1.5" /> Jobs étudiants</TabsTrigger>
          <TabsTrigger value="banques" data-testid="tab-banques"><Wallet size={14} className="mr-1.5" /> Banques étudiantes</TabsTrigger>
          <TabsTrigger value="prets" data-testid="tab-prets"><PiggyBank size={14} className="mr-1.5" /> Prêts étudiants</TabsTrigger>
          <TabsTrigger value="budget" data-testid="tab-budget"><Coins size={14} className="mr-1.5" /> Budget & économies</TabsTrigger>
        </TabsList>

        {/* Jobs */}
        <TabsContent value="jobs" className="pt-6 space-y-6">
          <Link
            to="/jobs-ai"
            className="block rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all"
            data-testid="ai-tools-cta"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Sparkles size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl font-bold">3 outils IA pour décrocher ton job</p>
                <p className="text-white/85 text-sm mt-1">Génère un CV étudiant, une lettre de motivation, et entraîne-toi à l'entretien avec Claude IA. Gratuit pour les Premium.</p>
              </div>
              <ArrowRight size={20} className="shrink-0 mt-2" />
            </div>
          </Link>
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5 text-slate-700 text-sm">
            <strong>Job étudiant</strong> : 15h/semaine max recommandé pour ne pas plomber tes notes. CDD, missions ponctuelles, télétravail, livraison… choisis ce qui colle à ton emploi du temps.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {JOB_PLATFORMS.map((p) => <LogoTile key={p.id} item={p} />)}
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-800 pt-2">Conseils pratiques</h3>
          <div className="space-y-3">
            {JOB_TIPS.map((t, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 bg-white p-5 flex gap-4" data-testid={`job-tip-${idx}`}>
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                  <Lightbulb size={18} />
                </div>
                <div>
                  <p className="font-display font-semibold text-slate-900">{t.title}</p>
                  <p className="text-slate-600 text-sm mt-1">{t.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Banques */}
        <TabsContent value="banques" className="pt-6 space-y-6">
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5 text-slate-700 text-sm">
            <strong>Choisis ta banque comme un pro :</strong> compare les frais (carte, tenue de compte, retraits), regarde les bonus d'ouverture (souvent 80-150 €), évite les agences classiques sauf si tu as besoin d'un conseiller physique.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {STUDENT_BANKS.map((b) => <LogoTile key={b.id} item={b} />)}
          </div>
        </TabsContent>

        {/* Prêts */}
        <TabsContent value="prets" className="pt-6 space-y-6">
          <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-5 text-slate-700 text-sm">
            <ShieldCheck size={16} className="inline text-yellow-700 mr-1.5" />
            <strong>Prêt étudiant garanti par l'État :</strong> jusqu'à 20 000 € sans caution ni garant — la solution si tes parents ne peuvent pas se porter garants. Disponible chez Société Générale, BNP, Crédit Mutuel, BFM (réseau partenaire).
            <a href="https://www.etudiant.gouv.fr/fr/pret-bancaire-etudiant-garanti-par-l-etat-376" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 ml-2 text-yellow-900 underline">
              En savoir plus <ArrowRight size={12} />
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {STUDENT_LOANS.map((l) => <LogoTile key={l.id} item={l} />)}
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-800 pt-2">À savoir avant de signer</h3>
          <div className="space-y-3">
            {LOAN_TIPS.map((t, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 bg-white p-4 flex gap-3 items-start" data-testid={`loan-tip-${idx}`}>
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                <p className="text-slate-700 text-sm">{t}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Budget */}
        <TabsContent value="budget" className="pt-6 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {MONEY_TIPS.map((m) => (
              <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-5 flex gap-4" data-testid={`money-tip-${m.id}`}>
                <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Coins size={18} />
                </div>
                <div>
                  <p className="font-display font-semibold text-slate-900">{m.title}</p>
                  <p className="text-slate-600 text-sm mt-1">{m.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-800 pt-2">Apps & réductions à installer</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {SAVINGS_APPS.map((s) => <LogoTile key={s.id} item={s} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
